import React, {useEffect, useState, useCallback } from "react";
import axios from "axios";
import Toolbar from "./Toolbar";
import EventCard from "./EventCard";
import OTGToolbar from "./OTGToolBar";
import {toast, ToastContainer} from "react-toastify";
import { useLocation } from "react-router-dom";

const EventList = () => {

    const location = useLocation();

    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [userName, setUserName] = useState("");
    const [userId, setUserId] = useState("");
    const [pathToAvatar, setPathToAvatar] = useState("");
    const [canCreate, setCanCreate] = useState("");
    const [selectedRegion, setSelectedRegion] = useState(""); // New state for region filter
    const [searchTerm, setSearchTerm] = useState(location.state?.searchTerm || "");
    const [isAdmin, setIsAdmin] = useState(true);

    const [filters, setFilters] = useState(location.state?.filters || {
        difficulty: null,
        dogs      : false,
        private    : false,
        children  : false,
        myevents : null
    });

    const handleComplicatedSearch = useCallback(async (query, region, terrain, dogs, isPrivate, children, myevents) => {
        try {
            let url = `/otgservices/getOTGEvents.php?search=${encodeURIComponent(query)}`;

            // Add region to query if selected
            if (region) {
                url += `&region=${encodeURIComponent(region)}`;
            }

            if (terrain) {
                url += '&terrain=' + terrain;
            }

            if (dogs) {
                url += '&dogs=1';
            }

            if (isPrivate) {
                url += '&private=1';
            }

            if (children) {
                url += '&children=' + children;
            }

            if (myevents) {
                url += '&myevents=1'
            }

            const response = await fetch(url);
            const data = await response.json();
            if (data.rows.length) {
                setEvents(data.rows);
                if (data.userName) setUserName(data.userName);
                if (data.userId) setUserId(data.userId);
                if (data.canCreate) setCanCreate(data.canCreate);
                if (data.pathToAvatar) setPathToAvatar(data.pathToAvatar);
                setIsAdmin(data.isadmin || false);
            } else {
                if (data.message) {
                    toast.error("Error: " + data.message);
                } else {
                   // toast.error("No matching events found");
                }
                setEvents([]);
            }
            setLoading(false);
        } catch (error) {
            console.error("Search failed", error);
        }
    }, []); // Empty dependency array since it doesn't depend on state

    const handleFilterChange = useCallback((newFilters) => {
        setFilters(newFilters);
        // Apply filtering logic to your events list here
    }, []);

    const handleDelete = async (event) => {
        const confirmed = window.confirm('Are you sure you want to cancel this event?');
        if (!confirmed) {
            return;
        }

        try {
            const deleteresponse = await fetch(`/otgservices/deleteEvent.php?&event_id=` + event.id);

            const data = await deleteresponse.json();

            if (data.status === "success") {
                toast.success(data.username + " has cancelled the event successfully!");
                // Handle case where are added
                const idToRemove = event.id;
                // remove the event
                setEvents(events => events.filter(event => event.id !== idToRemove));
            } else {
                toast.error("Failed to Delete event: " + (data.message || "Unknown error"));
            }
        } catch (error) {
            toast.error("Error Deleting event. Please try again.");
            console.error(error);
        }
    }

    const handleLeave = async (event) => {
        const confirmed = window.confirm('Are you sure you want to leave this event?');
        if (!confirmed) {
            return;
        }

        try {
            const leaveresponse = await fetch(`/otgservices/leaveEvent.php?&event_id=` + event.id);

            const data = await leaveresponse.json();

            let eventId = event.id;

            if (data.status === "success") {
                toast.success(" You have left the event successfully!");
                var newGoing = data.newgoinglist;
                var newWaiting = data.newwaitinglist;
                var newNotGoing = data.newnotgoinglist;

                if (!newWaiting) {
                    newWaiting = "";
                }

                if (!newGoing) {
                    newGoing = "";
                }

                if (!newNotGoing) {
                    newNotGoing = "";
                }

                setEvents(events =>
                    events.map(event => {
                        if (event.id === eventId) {
                            return {...event, going: newGoing, waiting: newWaiting, notgoing : newNotGoing, canJoin: true};
                        }
                        return event;
                    })
                );
            } else {
                toast.error("Failed to Leave event: " + (data.message || "Unknown error"));
            }
        } catch (error) {
            toast.error("Error Leaving event. Please try again.");
            console.error(error);
        }
    }

    const handleSearch = useCallback(async (query, region) => {
        try {
            setSearchTerm(query);
            await handleComplicatedSearch(query, region, filters.difficulty, filters.dogs, filters.private, filters.children, filters.myevents);
        } catch (error) {
            console.error("Search failed", error);
        }
    }, [filters.difficulty, filters.dogs, filters.private, filters.children, filters.myevents, handleComplicatedSearch]);

    const handleRegionChange = async (region) => {
        setSelectedRegion(region);
    };

    // Remove handleSearch from useEffect dependencies and inline the search logic
    useEffect(() => {
        const performSearch = async () => {
            if ((searchTerm != null && searchTerm !== "") || (filters && Object.values(filters).some(v => v))
                            || (selectedRegion !== null && selectedRegion !== "")  ) {
                let query = searchTerm || "";
                await handleComplicatedSearch(query, selectedRegion, filters.difficulty, filters.dogs, filters.private, filters.children, filters.myevents);
                return;
            }

            // Original axios call for initial load
            try {
                const response = await axios.get("/otgservices/getOTGEvents.php");
                setEvents(response.data.rows);

                if (response.data.userName) setUserName(response.data.userName);
                if (response.data.userId) setUserId(response.data.userId);
                if (response.data.canCreate) setCanCreate(response.data.canCreate);
                if (response.data.pathToAvatar) setPathToAvatar(response.data.pathToAvatar);
                setIsAdmin(response.data.isadmin || false);
                setLoading(false);

                // Handle hash scrolling
                const hash = window.location.hash;
                if (hash && hash.startsWith('#event-')) {
                    setTimeout(() => {
                        const elementId = hash.substring(1);
                        const element = document.getElementById(elementId);
                        if (element) {
                            element.scrollIntoView({ behavior: 'smooth' });
                        }
                    }, 150);
                }
            } catch (error) {
                console.error("Error fetching events:", error);
                setLoading(false);
            }
        };

        performSearch();
    }, [searchTerm, filters, selectedRegion, handleComplicatedSearch]); // Remove handleSearch from dependencies

    return (
        <div className="p-2 md:p-6 bg-gray-100 min-h-screen">
            <OTGToolbar isAdmin={isAdmin} />
            <ToastContainer/>
            <Toolbar
                onSearch={handleSearch}
                userName={userName}
                userId={userId}
                isAdmin={isAdmin}
                pathToAvatar={pathToAvatar}
                canCreate={canCreate}
                onRegionChange={handleRegionChange}
                selectedRegion={selectedRegion}
                onFilterChange={handleFilterChange}
                searchTerm={searchTerm}
                onSearchTermChange={setSearchTerm}
                filters={filters}
            />
            <br/>
            {loading ? (
                <p className="text-center">Loading events...</p>
            ) : (
                <div className="max-w-screen-xl mx-auto">
                    {events.length === 0 ? (
                        <div className="text-center py-8">
                            <p className="text-gray-500 text-lg">No Events found</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 lg:grid-cols-1 gap-6 auto-rows-auto min-w-0">
                            {events.map((event) => (
                                <EventCard key={event.id} onDelete={handleDelete} onLeave={handleLeave}
                                           event={event} userId={userId}
                                           searchTerm={searchTerm}
                                           filters={filters}
                                />
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default EventList;